/* eslint-disable react/no-unstable-nested-components */
import { localeDate, localeTime } from 'src/localization/locale';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { OutboundStatusBadge } from 'src/components/layouts/OutboundTable/OutboundTable';
import {
  SaleOrder,
  useCancelSaleOrder,
  useConfirmSaleOrder,
  useSaleOrdersQuery,
  useSaleOrderMutations,
  useSetDraftSaleOrder,
  fetchAllSaleOrderIds,
} from 'src/services/saleOrder';
import { createColumnHelper } from '@tanstack/react-table';
import { getCountryImageSource, Status } from 'src/utils/general';
import Image from 'src/components/ui/Image/Image';
import Icon from 'src/components/ui/Icon/Icon';
import { useLocation, useNavigate } from 'react-router-dom';
import TrackingListModal from 'src/components/modals/TrackingListModal/TrackingListModal';
import { useQuickFilterQuery } from 'src/services/utility';
import { toast } from 'react-toastify';
import styles from './styles/Outbound.module.scss';
import { useNavigationItems } from '../hooks/useNavigationItems';
import { DataView } from '../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { useDataViewController } from '../hooks/useDataViewController';
import { SearchModels } from '../components/ui/TableOverview/utils/SearchFieldRegistry';
import { OverlayNavProvider } from '../providers/OverlayNavProvider';

const formatEuro = (value: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value);
};

function Outbound() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isTrackingModalOpen, setTrackingModalOpen] = React.useState(false);
  const [selectedShippingLines, setSelectedShippingLines] = React.useState<any[]>([]);

  const { data: quickFilters } = useQuickFilterQuery('sale.order');
  const onTrackingClick = (lines: any[]) => {
    setSelectedShippingLines(lines);
    setTrackingModalOpen(true);
  };

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('outbound');

  const { mutateAsync: cancelSaleOrders } = useCancelSaleOrder();
  const { mutateAsync: confirmSaleOrders } = useConfirmSaleOrder();
  const { mutateAsync: setDraftSaleOrders } = useSetDraftSaleOrder();
  const { duplicateSaleOrders } = useSaleOrderMutations();

  const columnHelper = createColumnHelper<SaleOrder>();

  const columns = [
    columnHelper.accessor(row => row.id, {
      id: 'id',
      size: 80,
      enableHiding: false,
      meta: { label: t('general.reference') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14--bold text-full">{info.row.original.name}</span>,
    }),
    columnHelper.accessor(row => row.partner?.name, {
      id: 'partner_name',
      meta: { label: t('general.name') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full truncate"> {info.row.original.partner?.name} </span>
          <span className="typo-text-12 text-faded truncate">
            {info.row.original.partner?.address.street}
            <br />
            {info.row.original.partner?.address.zip_code} {info.row.original.partner?.address.city} |{' '}
            {info.row.original.partner?.address.country_code}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.date_order, {
      id: 'date_order',
      sortingFn: 'datetime',
      meta: { label: t('general.date_order') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const value = info.getValue();
        const date = value ? new Date(value) : null;
        const formattedDate = date ? localeDate(date) : '';
        const formattedTime = date ? localeTime(date) : '';

        return (
          <div className="flex flex-col gap-y-8">
            <span className="typo-text-14 text-full">{formattedDate || ''}</span>
            <span className="typo-text-12 text-faded">{formattedTime || ''}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.create_date, {
      id: 'create_date',
      sortingFn: 'datetime',
      meta: { label: t('general.import_date') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const value = info.getValue();
        const date = value ? new Date(value) : null;
        const formattedDate = date ? localeDate(date) : '';
        const formattedTime = date ? localeTime(date) : '';

        return (
          <div className="flex flex-col gap-y-8">
            <span className="typo-text-14 text-full">{formattedDate || ''}</span>
            <span className="typo-text-12 text-faded">{formattedTime || ''}</span>
          </div>
        );
      },
    }),
    // columnHelper.accessor(row => row.commitment_date, {
    //   id: 'commitment_date',
    //   sortingFn: 'datetime',
    //   meta: { label: t('general.commitment_date') },
    //   header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
    //   cell: info => {
    //     const value = info.getValue();
    //     const date = value ? new Date(value) : null;
    //     const formattedDate = date?.toLocaleDateString();
    //     const formattedTime = date?.toLocaleTimeString();
    //
    //     return (
    //       <div className="flex flex-col gap-y-8">
    //         <span className="typo-text-14 text-full">{formattedDate || ''}</span>
    //         <span className="typo-text-12 text-faded">{formattedTime || ''}</span>
    //       </div>
    //     );
    //   },
    // }),

    columnHelper.accessor(row => row.carrier?.name, {
      id: 'carrier_id',
      size: 80,
      enableSorting: false,
      meta: { label: t('general.carrier') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded w-full text-center">{column.columnDef.meta?.label}</span>
      ),
      cell: info => (
        <span className="typo-text-14 text-full flex justify-center">
          <Image
            className={styles.carrier}
            imageUrl={info.row.original.carrier?.logo_url || ''}
            alt={info.row.original.carrier?.name || 'carrier-logo'}
            fallbackComponent={<Icon name="package" width={36} height={36} />}
          />
        </span>
      ),
    }),
    columnHelper.accessor(row => (row.carrier as any)?.shipping_lines, {
      id: 'shipping_lines',
      size: 120,
      enableResizing: true,
      enableSorting: false,
      meta: { label: t('general.tracking') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded text-center w-full">{column.columnDef.meta?.label}</span>
      ),
      cell: info => {
        const data = (info.row.original.carrier as any)?.shipping_lines || [];

        return (
          <span className="typo-text-14 text-full flex justify-center">
            {(info.row.original?.carrier as any)?.shipping_lines?.length && (
              <Button
                icon="package"
                title={(info.row.original?.carrier as any)?.shipping_lines?.length.toString() || '0'}
                tone={ButtonToneProps.SECONDARY}
                onClick={event => {
                  event.stopPropagation();
                  onTrackingClick(data);
                }}
              />
            )}
          </span>
        );
      },
    }),
    columnHelper.accessor(row => row.warehouse?.country_code, {
      id: 'warehouse_country',
      size: 120,
      enableSorting: false,
      meta: { label: t('general.source_country') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded w-full text-center">{column.columnDef.meta?.label}</span>
      ),
      cell: info => {
        return (
          <span className="typo-text-14 text-full flex justify-center">
            <Button fullOpacity icon={getCountryImageSource(info.getValue())} tone={ButtonToneProps.SECONDARY} />
          </span>
        );
      },
    }),
    columnHelper.accessor(row => row.partner?.address.country_code, {
      id: 'partner_id',
      size: 120,
      enableSorting: false,
      meta: { label: t('general.destination_country') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded w-full text-center">{column.columnDef.meta?.label}</span>
      ),
      cell: info => {
        const value = info.getValue();
        const imageSrc = getCountryImageSource(value);
        return (
          value &&
          imageSrc && (
            <span className="typo-text-14 text-full flex justify-center">
              <Button fullOpacity icon={getCountryImageSource(value)} tone={ButtonToneProps.SECONDARY} />
            </span>
          )
        );
      },
    }),
    columnHelper.accessor(row => row.order_lines?.length, {
      id: 'order_line',
      size: 120,
      meta: { label: t('general.products') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded w-full text-center">{column.columnDef.meta?.label}</span>
      ),
      cell: info => {
        return (
          <div className="w-full text-center display-inline">
            <span className="typo-text-14 text-full"> {info.getValue()} </span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.amount_total, {
      id: 'amount_total',
      size: 80,
      meta: { label: t('general.amount_total') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded w-full text-center">{column.columnDef.meta?.label}</span>
      ),
      cell: info => (
        <div className="w-full text-center display-inline">
          <span className="typo-text-14 text-full">{formatEuro(info.getValue() || 0)}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.connector, {
      id: 'connector_id',
      size: 80,
      enableSorting: false,
      meta: { label: t('general.channel') },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded w-full text-center">{column.columnDef.meta?.label}</span>
      ),
      cell: info => (
        <span className="typo-text-14 text-full w-full flex gap-x-6 items-center justify-center">
          <Image
            imageUrl={info.getValue() || ''}
            alt="logo"
            className="w-[2rem]"
            fallbackComponent={<Icon name="api" width={20} height={20} />}
          />
        </span>
      ),
    }),
    columnHelper.accessor(row => row.fulfillment_state, {
      id: 'fulfillment_state',
      meta: { label: t('general.status') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        return (
          <div className="flex">
            <span className="typo-text-14 text-full capitalize">
              <OutboundStatusBadge status={info.getValue()} />
            </span>
          </div>
        );
      },
    }),
  ];

  const location = useLocation();
  const preSelectedTab = location.state?.preselectedTab;
  const preSelectedDomain = location.state?.preselectedDomain;
  const controller = useDataViewController({
    tab: preSelectedTab,
    domain: preSelectedDomain,
  });

  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);

  return (
    <OverlayNavProvider entityIds={entityIds} basePath="/outbound">
      <div className="space-y-4">
        <DataView<SaleOrder>
          onDataLoaded={items => setEntityIds(items.map(i => i.id).filter((id): id is number => id != null))}
          initialHiddenColumns={['amount_total']}
          allowBulkActions={accessRights?.read_write}
          bulkIdField="id"
          fetchAllBulkIds={fetchAllSaleOrderIds}
          filterModelId="sale.order"
          kpiModel="sale.order"
          headline={t('general.outbound_deliveries')}
          emptyState={<NoDataPlaceholder headline={t('general.empty_outbound')} />}
          modelKey={SearchModels.SaleOrder}
          controller={controller}
          columns={columns}
          useDataQuery={useSaleOrdersQuery}
          tabs={[
            { label: t('general.all'), key: 'all', value: quickFilters?.map(q => q.value).reduce((a, b) => a + b, 0) },
            ...(quickFilters ?? []),
          ]}
          getDomainForTab={(tabKey: string, index: number) =>
            index === 0 ? '[]' : `[("fulfillment_state", "=", "${tabKey}")]`
          }
          actions={[
            {
              title: t('general.create'),
              menuItems: [
                {
                  label: t('outbound.create_cta'),
                  onClick: () => {
                    navigate('/outbound/create');
                  },
                },
                {
                  label: t('outbound.import_cta'),
                  onClick: () => navigate('/outbound/import'),
                },
              ],
              icon: 'plus',
              showInToolbar: accessRights?.read_write,
            },
            {
              title: t('general.view_details'),
              icon: 'edit',
              isRowClickAction: true,
              onExecute: selected => {
                if (selected.length > 0) {
                  navigate(`/outbound/${selected[0].id}`);
                }
              },
              showInContextMenu: true,
            },
            {
              title: t('general.confirm_sale_order_title'),
              titlePlural: t('general.confirm_sale_orders_title'),
              icon: 'checkmark',
              showInContextMenu: accessRights?.read_write,
              bulkSupport: true,
              isDisabled: (selected: SaleOrder[]) => {
                const confirmable = selected.map(
                  s =>
                    !s.wms_hold &&
                    (s.fulfillment_state.key === Status.DRAFT ||
                      s.fulfillment_state.key === Status.UNDER_INVESTIGATION),
                );
                return confirmable.some(c => !c);
              },
              disabledHint: t('general.confirm_sale_order_disabled_hint'),
              onExecute: async selected => {
                const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
                try {
                  confirmSaleOrders({
                    params: {
                      path: {
                        model: 'sale.order',
                      },
                    },
                    body: ids,
                  });
                  toast.success(t('success.confirm_sale_order_success'));
                } catch (err: any) {
                  toast.error(`${t('errors.confirm_sale_order_failed')}: ${err.detail}`, { autoClose: 5000 });
                }
              },
              confirmSettings: {
                renderTitle: selected => {
                  if (selected.length > 1) {
                    return t('general.confirm_sale_orders_title');
                  }
                  return t('general.confirm_sale_order_title');
                },
                renderContent: selected => {
                  if (selected.length > 1) {
                    return t('general.confirm_sale_orders_text');
                  }
                  return t('general.confirm_sale_order_text');
                },
                confirmLabel: t('general.confirm'),
                cancelLabel: t('general.cancel'),
              },
            },
            {
              title: t('general.set_sale_order_as_draft'),
              titlePlural: t('general.set_sale_orders_as_draft'),
              icon: 'note',
              showInContextMenu: accessRights?.read_write,
              bulkSupport: true,
              isDisabled: (selected: SaleOrder[]) => {
                const editable = selected.map(
                  s =>
                    s.is_editable &&
                    (s.fulfillment_state.key === Status.DRAFT ||
                      s.fulfillment_state.key === Status.CANCEL ||
                      s.fulfillment_state.key === Status.OPEN ||
                      s.fulfillment_state.key === Status.NOT_AVAILABLE ||
                      s.fulfillment_state.key === Status.UNDER_INVESTIGATION),
                );
                return editable.some(c => !c);
              },
              disabledHint: t('general.set_draft_sale_order_disabled_hint'),
              onExecute: async selected => {
                const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
                try {
                  await setDraftSaleOrders({
                    params: {
                      path: {
                        model: 'sale.order',
                      },
                    },
                    body: ids,
                  });
                  toast.success(t('success.update_sale_order_success'));
                } catch (err: any) {
                  toast.error(`${t('errors.update_sale_order_failed')}: ${err.detail}`, { autoClose: 5000 });
                }
              },
              confirmSettings: {
                renderTitle: selected => {
                  if (selected.length > 1) {
                    return t('general.set_sale_orders_as_draft');
                  }
                  return t('general.set_sale_order_as_draft');
                },
                renderContent: selected => {
                  if (selected.length > 1) {
                    return t('general.set_draft_sale_orders_text');
                  }
                  return t('general.set_draft_sale_order_text');
                },
                confirmLabel: t('general.confirm'),
                cancelLabel: t('general.cancel'),
              },
            },
            {
              title: t('general.cancel_sale_order'),
              titlePlural: t('general.cancel_sale_orders'),
              icon: 'close',
              showInContextMenu: accessRights?.read_write,
              bulkSupport: true,
              isDisabled: (selected: SaleOrder[]) => {
                const cancelable = selected.map(
                  s =>
                    s.fulfillment_state.key === Status.OPEN ||
                    s.fulfillment_state.key === Status.UNDER_INVESTIGATION ||
                    s.fulfillment_state.key === Status.DRAFT ||
                    s.fulfillment_state.key === Status.NOT_AVAILABLE,
                );
                return cancelable.some(c => !c);
              },
              disabledHint: t('general.cancel_sale_order_disabled_hint'),
              onExecute: async selected => {
                const ids = selected.map(s => s.id).filter(id => id !== undefined && id !== null);
                try {
                  await cancelSaleOrders({
                    params: {
                      path: {
                        model: 'sale.order',
                      },
                    },
                    body: ids,
                  });
                  toast.success(t('success.delete_sale_order_success'));
                } catch (err: any) {
                  toast.error(`${t('errors.delete_sale_order_failed')}: ${err.detail}`, { autoClose: 5000 });
                }
              },
              confirmSettings: {
                renderTitle: selected => {
                  if (selected.length > 1) {
                    return t('general.cancel_sale_orders');
                  }
                  return t('general.cancel_sale_order');
                },
                renderContent: selected => {
                  if (selected.length > 1) {
                    return t('general.cancel_sale_orders_text');
                  }
                  return t('general.cancel_sale_order_text');
                },
                confirmLabel: t('general.confirm'),
                cancelLabel: t('general.cancel'),
              },
            },
            {
              title: t('outbound.duplicate'),
              titlePlural: t('outbound.duplicate_plural'),
              icon: 'duplicate',
              // eslint-disable-next-line consistent-return
              onExecute: selected => {
                const ids = selected.map(s => s.id).filter((id): id is number => id != null);
                if (ids.length > 0) {
                  return duplicateSaleOrders(ids).then(() => {});
                }
              },
              isRowClickAction: false,
              showInContextMenu: accessRights?.read_write,
              bulkSupport: true,
            },
          ]}
          onRowClick={(row: SaleOrder) => navigate(`/outbound/${row.id}`)}
        />
      </div>

      <TrackingListModal
        isOpen={isTrackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        items={selectedShippingLines}
      />
    </OverlayNavProvider>
  );
}

export default Outbound;
